import { Check } from 'lucide-react';

import { useTranslation } from '@/i18n/i18n';
import { useModuleImpression } from '@/hooks/useModuleImpression';

/**
 * The "Ty zatwierdzasz" mechanic as a visual proof moment (BOO-3240 U6,
 * autoplan decisions #14/#17/#18/#29, KTD4).
 *
 * Four steps in the Dowód mono/ledger idiom — agent liczy → cytat ustawy →
 * Ty zatwierdzasz → gotowe do złożenia (składasz sam w e-Deklaracjach lub PUE ZUS) — plus a VISIBLE muted failure branch (4a):
 * a skeptical reader's next question after four clean steps is "show me it
 * breaking", so the rejection path is content, not a caption.
 *
 * The status frame is TYPOGRAPHIC evidence (CalcEvidenceCard pattern): the
 * fields of a prepared draft (document, status "projekt — czeka na
 * zatwierdzenie"), with all identifiers omitted. No image asset, and no UPO or
 * "submitted" state is shown: the shipped copy is prepare-only.
 */
const STEP_KEYS = [
  'approvalFlow.step1',
  'approvalFlow.step2',
  'approvalFlow.step3',
  'approvalFlow.step4',
] as const;

export const ApprovalFlowVisual = () => {
  const { t } = useTranslation();
  const impressionRef = useModuleImpression<HTMLDivElement>('approval_flow');

  return (
    <div
      ref={impressionRef}
      data-slot="approval-flow"
      className="rounded-2xl border border-foreground/20 bg-card p-6"
    >
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        {t('approvalFlow.kicker')}
      </p>
      {/* sr-only narrative for AT (decision #17) */}
      <p className="sr-only">{t('approvalFlow.aria')}</p>
      <ol className="mt-4 space-y-3">
        {STEP_KEYS.map((key, index) => (
          <li key={key} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border border-border font-mono text-xs tabular-nums text-muted-foreground"
            >
              {index + 1}
            </span>
            <div>
              <p className="font-medium text-foreground">
                {t(key)}
                {index === 2 && (
                  <Check className="ml-1.5 inline size-4 text-success" aria-hidden="true" />
                )}
              </p>
              <p className="text-sm text-muted-foreground">{t(`${key}.detail`)}</p>
            </div>
          </li>
        ))}
        {/* 4a — the visible failure branch (decision #14): muted, but content. */}
        <li
          data-slot="approval-flow-failure"
          className="ml-9 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3"
        >
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            {t('approvalFlow.failure.kicker')}
          </p>
          <p className="mt-1 text-sm text-foreground">{t('approvalFlow.failure.detail')}</p>
        </li>
      </ol>
      {/* Typographic draft-status evidence (KTD4) — no identifiers. */}
      <div className="mt-5 border-t border-border pt-4 font-mono text-sm tabular-nums">
        <div className="flex items-baseline justify-between gap-6">
          <span className="text-muted-foreground">{t('approvalFlow.upo.docLabel')}</span>
          <span className="text-foreground">PIT-36 (korekta)</span>
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-6">
          <span className="text-muted-foreground">{t('approvalFlow.upo.statusLabel')}</span>
          <span className="text-foreground">{t('approvalFlow.upo.statusValue')}</span>
        </div>
      </div>
    </div>
  );
};
